import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { MessageCircle, Send, ArrowLeft, Archive, Trash2, Inbox, Search } from 'lucide-react';
import { formatDate } from '@/lib/format';
import { Layout } from '@/components/Layout';
import { useSEO } from '@/lib/useSEO';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Buyer↔seller messaging center. Uses 5s polling of the active thread and
 * refreshes the list every 15s.  Replaces the previous WhatsApp buttons so
 * every conversation stays inside AutoMarket.
 */
export default function Messages() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null); // full thread
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [viewArchived, setViewArchived] = useState(false);
  // Filtro Todos | Leídos | No leídos (aplica sobre la vista actual — Activos o Archivados)
  const [readFilter, setReadFilter] = useState('all'); // 'all' | 'read' | 'unread'
  const [search, setSearch] = useState('');
  // Ref al CONTENEDOR interno de mensajes para hacer scroll SOLO dentro del panel,
  // sin arrastrar la página completa hacia abajo cuando abrimos un chat.
  const messagesContainerRef = useRef(null);
  const endRef = useRef(null);

  // Filtrado client-side: aplica leídos/no leídos y búsqueda por nombre / producto / último mensaje.
  const filteredThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    return threads.filter((t) => {
      if (readFilter === 'unread' && !(t.unread > 0)) return false;
      if (readFilter === 'read' && (t.unread > 0)) return false;
      if (!q) return true;
      const counterpart = (user?.role === 'buyer' ? t.seller_name : t.buyer_name) || '';
      const hay = `${counterpart} ${t.product_name || ''} ${t.last_message || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [threads, readFilter, search, user?.role]);

  // Scroll interno al final del panel (SIN mover la página).
  const scrollMessagesToBottom = () => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  useSEO({ title: 'Mensajes | AutoMarket Colombia', canonical: '/mensajes' });

  const loadThreads = async () => {
    if (!user) return;
    const params = viewArchived ? { archived: true } : {};
    const { data } = await api.get('/chat/threads', { params });
    setThreads(data.items || []);
  };
  const openThread = async (thread_id) => {
    const { data } = await api.get(`/chat/threads/${thread_id}`);
    setActive(data.thread);
    setMessages(data.messages || []);
    api.post(`/chat/threads/${thread_id}/read`).catch(() => {});
    // Scroll SOLO dentro del panel de mensajes (no de la ventana). Sin `behavior: smooth`
    // ni `scrollIntoView` para evitar que la página se desplace al abrir el chat.
    setTimeout(scrollMessagesToBottom, 60);
  };

  const archiveThread = async (thread_id, archive) => {
    try {
      await api.post(`/chat/threads/${thread_id}/archive`, null, { params: { archive } });
      toast.success(archive ? 'Conversación archivada' : 'Conversación restaurada');
      if (active?.thread_id === thread_id) setActive(null);
      loadThreads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al archivar');
    }
  };

  const deleteThread = async (thread_id) => {
    if (!window.confirm('¿Eliminar esta conversación de tu bandeja? Podrá reaparecer si la contraparte te escribe.')) return;
    try {
      await api.delete(`/chat/threads/${thread_id}`);
      toast.success('Conversación eliminada');
      if (active?.thread_id === thread_id) setActive(null);
      loadThreads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  useEffect(() => { loadThreads(); }, [viewArchived, user?.user_id]); // eslint-disable-line
  // Auto-open thread if `?open=<thread_id>` is in the URL
  useEffect(() => {
    const openId = sp.get('open');
    if (openId) openThread(openId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);
  // Poll active thread every 5s
  useEffect(() => {
    if (!active?.thread_id) return;
    const id = setInterval(async () => {
      const { data } = await api.get(`/chat/threads/${active.thread_id}`);
      setMessages(data.messages || []);
    }, 5000);
    return () => clearInterval(id);
  }, [active?.thread_id]);
  // Poll threads list every 15s
  useEffect(() => {
    const id = setInterval(loadThreads, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    const text = draft.trim();
    if (!text || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/threads/${active.thread_id}/messages`, { text });
      setMessages([...messages, data.message]);
      setDraft('');
      setTimeout(scrollMessagesToBottom, 30);
      loadThreads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar');
    } finally {
      setSending(false);
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="mx-auto max-w-md py-24 text-center">
          <p className="text-slate-600">Inicia sesión para ver tus mensajes.</p>
          <Link to="/login" className="mt-4 inline-block underline text-slate-900 font-semibold">Ingresar</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Chat interno</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Mensajes</h1>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-[300px_1fr] gap-4 border border-slate-200 bg-white min-h-[500px]">
          <div className="border-r border-slate-200 overflow-y-auto max-h-[70vh]" data-testid="messages-thread-list">
            <div className="flex border-b border-slate-200 bg-slate-50">
              <button
                data-testid="messages-tab-active"
                onClick={() => { setViewArchived(false); setActive(null); }}
                className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${!viewArchived ? 'text-slate-900 bg-white border-b-2 border-orange-500' : 'text-slate-500'}`}
              >
                <Inbox className="inline h-3 w-3 mr-1" /> Activos
              </button>
              <button
                data-testid="messages-tab-archived"
                onClick={() => { setViewArchived(true); setActive(null); }}
                className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${viewArchived ? 'text-slate-900 bg-white border-b-2 border-orange-500' : 'text-slate-500'}`}
              >
                <Archive className="inline h-3 w-3 mr-1" /> Archivados
              </button>
            </div>
            {/* Buscador */}
            <div className="p-2 border-b border-slate-100 bg-white">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input
                  data-testid="messages-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nombre, producto o mensaje…"
                  className="pl-8 h-8 rounded-none text-xs"
                />
              </div>
            </div>
            {/* Filtro Todos / Leídos / No leídos */}
            <div className="flex text-[10px] font-bold uppercase tracking-wider border-b border-slate-100 bg-white">
              {[
                { v: 'all', l: 'Todos' },
                { v: 'unread', l: 'No leídos' },
                { v: 'read', l: 'Leídos' },
              ].map((opt) => (
                <button
                  key={opt.v}
                  data-testid={`messages-filter-${opt.v}`}
                  onClick={() => setReadFilter(opt.v)}
                  className={`flex-1 py-1.5 transition-colors ${readFilter === opt.v ? 'text-orange-600 border-b-2 border-orange-500' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
            {filteredThreads.length === 0 && (
              <div className="p-8 text-center text-slate-500 text-sm" data-testid="messages-empty-state">
                <MessageCircle className="h-8 w-8 mx-auto text-slate-300" />
                <p className="mt-2">
                  {threads.length === 0
                    ? (viewArchived ? 'No tienes conversaciones archivadas.' : 'Aún no tienes conversaciones.')
                    : 'Sin resultados para el filtro/búsqueda.'}
                </p>
              </div>
            )}
            {filteredThreads.map((t) => (
              <button
                key={t.thread_id}
                onClick={() => openThread(t.thread_id)}
                data-testid={`messages-thread-${t.thread_id}`}
                className={`w-full text-left p-3 border-b border-slate-100 hover:bg-slate-50 ${active?.thread_id === t.thread_id ? 'bg-orange-50' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-slate-900 truncate text-sm">
                    {user.role === 'buyer' ? t.seller_name : t.buyer_name}
                  </div>
                  {t.unread > 0 && (
                    <span className="bg-orange-500 text-white text-[10px] font-bold rounded-full px-2 py-0.5">
                      {t.unread}
                    </span>
                  )}
                </div>
                {t.product_name && <div className="text-[11px] text-slate-500 truncate">Sobre: {t.product_name}</div>}
                <div className="text-xs text-slate-600 truncate mt-1">{t.last_message || 'Sin mensajes aún'}</div>
                {t.last_message_at && <div className="text-[10px] text-slate-400 mt-0.5">{formatDate(t.last_message_at)}</div>}
              </button>
            ))}
          </div>

          <div className="flex flex-col">
            {!active ? (
              <div className="p-16 text-center text-slate-500 text-sm">
                Selecciona una conversación
              </div>
            ) : (
              <>
                <div className="border-b border-slate-200 p-3 flex items-center gap-2">
                  <button className="md:hidden" onClick={() => setActive(null)}><ArrowLeft className="h-4 w-4" /></button>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">
                      {user.role === 'buyer' ? active.seller_name : active.buyer_name}
                    </div>
                    {active.product_name && (
                      <div className="text-xs text-slate-500 truncate">Sobre: {active.product_name}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      data-testid="messages-archive-btn"
                      onClick={() => archiveThread(active.thread_id, !viewArchived)}
                      className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                      title={viewArchived ? 'Restaurar a activos' : 'Archivar conversación'}
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                    <button
                      data-testid="messages-delete-btn"
                      onClick={() => deleteThread(active.thread_id)}
                      className="p-2 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded"
                      title="Eliminar de mi bandeja"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div ref={messagesContainerRef} className="flex-1 p-4 space-y-2 overflow-y-auto max-h-[55vh]" data-testid="messages-panel">
                  {messages.map((m) => {
                    const mine = m.author_id === user.user_id;
                    return (
                      <div key={m.message_id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[75%] px-3 py-2 text-sm rounded ${mine ? 'bg-orange-500 text-slate-900' : 'bg-slate-100 text-slate-900'}`}>
                          <div className="whitespace-pre-wrap break-words">{m.text}</div>
                          <div className={`text-[10px] mt-1 ${mine ? 'text-slate-900/70' : 'text-slate-500'}`}>{formatDate(m.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={endRef} />
                </div>
                <div className="border-t border-slate-200 p-3 flex gap-2">
                  <textarea
                    data-testid="messages-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Escribe un mensaje…"
                    rows={2}
                    className="flex-1 border border-slate-200 rounded p-2 text-sm resize-none"
                  />
                  <Button data-testid="messages-send" onClick={send} disabled={sending || !draft.trim()} className="rounded-none bg-slate-900 text-white">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
